import { useState, useEffect, useRef } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Receipt, Camera, Loader2, Check, Trash2, Fuel, Wrench, Car,
  DollarSign, ChevronDown
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  mileage: "Mileage", gas: "Gas/Fuel", tools: "Tools",
  materials: "Materials", uniforms: "Uniforms", insurance: "Insurance",
  vehicle_maintenance: "Vehicle Maintenance", parking: "Parking",
  tolls: "Tolls", other: "Other"
};

const CATEGORY_ICONS = {
  mileage: Car, gas: Fuel, tools: Wrench, vehicle_maintenance: Wrench,
};

export default function TechExpenses({ requestId }) {
  const [expenses, setExpenses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("gas");
  const [showForm, setShowForm] = useState(false);
  const [mileage, setMileage] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchExpenses = async () => {
    try {
      const url = requestId ? `/expenses/job/${requestId}` : "/expenses/my?limit=20";
      const { data } = await API.get(url);
      setExpenses(requestId ? data.expenses : data.expenses);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => { fetchExpenses(); }, [requestId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    if (requestId) formData.append("request_id", requestId);
    formData.append("category", category);
    formData.append("description", description);
    formData.append("amount", amount || "0");
    formData.append("mileage", mileage || "0");

    try {
      await API.post("/expenses/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 45000,
      });
      toast.success("Expense uploaded");
      fetchExpenses();
      setShowForm(false);
      setDescription("");
      setAmount("");
      setMileage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to upload expense");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleApprove = async (id) => {
    try {
      await API.post(`/expenses/${id}/approve`);
      toast.success("Expense approved");
      fetchExpenses();
    } catch (err) { toast.error("Failed to approve"); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/expenses/${id}`);
      toast.success("Expense deleted");
      fetchExpenses();
    } catch (err) { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-3" data-testid="tech-expenses-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-zinc-400" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">
            Expenses ({expenses.length})
          </h4>
        </div>
        <Button size="sm" variant="outline" onClick={() => setShowForm(!showForm)}
          className="text-xs h-7" data-testid="toggle-expense-form">
          {showForm ? "Cancel" : "+ Add Expense"}
        </Button>
      </div>

      {showForm && (
        <Card className="border border-zinc-200" data-testid="expense-form">
          <CardContent className="p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-zinc-500 font-bold uppercase">Category</label>
                <select value={category} onChange={e => setCategory(e.target.value)}
                  className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs bg-white"
                  data-testid="expense-category-select">
                  {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-zinc-500 font-bold uppercase">Amount ($)</label>
                <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                  placeholder="0.00" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
                  data-testid="expense-amount-input" />
              </div>
            </div>
            {(category === "mileage" || category === "gas") && (
              <div>
                <label className="text-[10px] text-zinc-500 font-bold uppercase">Mileage</label>
                <input type="number" step="0.1" value={mileage} onChange={e => setMileage(e.target.value)}
                  placeholder="Miles driven" className="w-full mt-1 px-2 py-1.5 border border-zinc-300 rounded-lg text-xs"
                  data-testid="expense-mileage-input" />
              </div>
            )}
            <div>
              <label className="text-[10px] text-zinc-500 font-bold uppercase">Description</label>
              <Textarea value={description} onChange={e => setDescription(e.target.value)}
                rows={2} placeholder="Describe the expense..." className="mt-1 text-xs"
                data-testid="expense-description" />
            </div>
            <div className="flex gap-2">
              <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                className="hidden" onChange={handleUpload} />
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp"
                className="hidden" onChange={handleUpload} />
              <Button size="sm" variant="outline" onClick={() => openCamera(cameraInputRef)}
                disabled={uploading} className="flex-1 text-xs h-9"
                data-testid="expense-camera-btn">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Camera className="w-3.5 h-3.5 mr-1" /> Photo</>}
              </Button>
              <Button size="sm" variant="outline" onClick={() => openPhotoPicker(fileInputRef)}
                disabled={uploading} className="flex-1 text-xs h-9"
                data-testid="expense-upload-btn">
                Upload Receipt
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {expenses.length > 0 && (
        <div className="space-y-2">
          {expenses.map(exp => {
            const CatIcon = CATEGORY_ICONS[exp.category] || DollarSign;
            return (
              <Card key={exp.id} className="border border-zinc-200" data-testid={`expense-${exp.id}`}>
                <CardContent className="p-3">
                  <div className="flex items-start gap-3">
                    {exp.filename && (
                      <img src={`${API_BASE}/api/expenses/${exp.id}/image`}
                        alt="Receipt" className="w-14 h-14 rounded-lg object-cover bg-zinc-100 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <CatIcon className="w-3.5 h-3.5 text-zinc-400" />
                        <span className="text-xs font-medium text-zinc-700">
                          {CATEGORY_LABELS[exp.category] || exp.category}
                        </span>
                        {exp.is_approved && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">
                            <Check className="w-2.5 h-2.5 mr-0.5" /> Approved
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-zinc-600 truncate">{exp.description || exp.ai_description}</p>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-sm font-bold text-zinc-900">${exp.amount?.toFixed(2)}</span>
                        {exp.mileage > 0 && (
                          <span className="text-[10px] text-zinc-500">{exp.mileage} mi</span>
                        )}
                        <span className="text-[10px] text-zinc-400">
                          {new Date(exp.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!exp.is_approved && (
                        <Button size="sm" variant="outline" onClick={() => handleApprove(exp.id)}
                          className="text-[10px] h-6 px-2" data-testid={`approve-expense-${exp.id}`}>
                          <Check className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handleDelete(exp.id)}
                        className="text-[10px] h-6 px-2 text-red-500 hover:text-red-700"
                        data-testid={`delete-expense-${exp.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {expenses.length === 0 && !showForm && (
        <div className="text-center py-4 border border-dashed border-zinc-200 rounded-xl">
          <Receipt className="w-6 h-6 text-zinc-300 mx-auto mb-1.5" />
          <p className="text-xs text-zinc-400">No expenses logged yet</p>
          <p className="text-[10px] text-zinc-400 mt-0.5">Track mileage, gas, and other job expenses</p>
        </div>
      )}
    </div>
  );
}
